import { buildPrompt } from '@/ai/utils/promptBuilder'
import { examples } from '@/ai/prompts/common/examples'
import { sanitizeCardDescription } from '@/utils/sanitize'
import { commandNotes } from '@/ai/prompts/common/instructions'
import { systemMessages } from '@/ai/prompts/common/systemMessages'
import { getJsonOutputFormatting } from '@/ai/prompts/common/formatting'
import { createUserMessage, createSystemMessage } from '@/ai/utils/promptUtils'
import { buildWorkflowSpec, buildCardTypesMap } from '@/shared/utils/workflowSpec'
import { DomainModelRole } from '@/constants/cardTypes'

export const buildCommandPrompt = ({ eventId, workflow, cardTypes }) => {
  const messages = []

  const systemContent = buildPrompt([systemMessages.EVENT_MODELING_DDD_EXPERT])
  messages.push(createSystemMessage(systemContent))

  const promptSections = []

  const event = workflow.domainEvents.find((e) => e.id === eventId) || {}
  const cardTypesMap = buildCardTypesMap(cardTypes)

  const commandCard = (event.cards || []).find((c) => {
    return cardTypesMap[c.cardTypeId]?.domainModelRole === DomainModelRole.COMMAND
  })
  const aggregateCard = (event.cards || []).find((c) => {
    return cardTypesMap[c.cardTypeId]?.domainModelRole === DomainModelRole.AGGREGATE_ROOT
  })

  const commandName = sanitizeCardDescription(commandCard?.description || '')
  const aggregateName = sanitizeCardDescription(aggregateCard?.description || '')
  const laneMap = new Map((workflow.lanes || []).map((l) => [l.id, l.name]))
  const roleName = laneMap.get(event.laneId) || ''

  promptSections.push({
    title: 'Request',
    content: `Focus on domain event "${event.description}" (${roleName}). For this step in the process, create a set of input fields. You are designing a simple input form (or mockup or wireframe) with data fields to be filled in and submitted by the actor (or role), in order to invoke the command "${commandName}" on the aggregate "${aggregateName}", for this step of the process. Each data field is an input field that the user (or system, if automated) can fill in.`
  })

  promptSections.push({
    title: 'Current Workflow Specification (JSON format)',
    contentType: 'codeblock',
    language: 'json',
    content: buildWorkflowSpec(workflow, null, cardTypesMap) || '{}'
  })

  promptSections.push({
    title: 'Very Important Considerations',
    contentType: 'bullet',
    content: commandNotes
  })

  const commandExample = {
    required: ['customerName', 'productName', 'deliveryTime', 'deliveryAddress', 'quantity'],
    command: examples.commandExampleCreateOrder
  }

  promptSections.push({
    title: 'Formatting',
    flattenLines: false,
    content: getJsonOutputFormatting('command', JSON.stringify(commandExample))
  })

  const userContent = buildPrompt(promptSections)
  messages.push(createUserMessage(userContent))

  return messages
}
